/**
 * EscapeDestination — `/escape/destination/:slug` dedicated destination page.
 *
 * Founder ruling 19 Feb 2026 PM · Wave B-2 deliverable #1:
 *   Clicking a destination card from the `/escape` landing grid (or
 *   the Browse Escapes panel's Destinations tab) must route to a
 *   destination-specific results page · "Escapes in St Tropez" /
 *   "Escapes in Tokyo" / etc. The page mirrors Viator's destination
 *   pages but in calm Concierge® voice.
 *
 * What renders:
 *   1. Eyebrow + back link + brass headline `Escapes in <Destination>`
 *   2. Cinematic hero photo (the same Unsplash CDN as the tile)
 *   3. Quiet subline · "Tours, attractions and experiences curated by the Concierge®"
 *   4. The shared EscapeResultsToolbar (sort + chips + refine)
 *   5. Results grid (paginated · "Load more" pattern matches /escape)
 *   6. Relevance-gated Signature Escapes shelf · only shows when the
 *      destination matches a curated route (St Tropez → Riviera, etc.)
 *   7. Request modal · same EscapeRequestModal used elsewhere
 *
 * Doctrine preserved:
 *   - Pagination metadata is real (Viator totalCount) · not faked.
 *   - Signature shelf goes BELOW results when relevant, never above.
 *   - No URL fabrication · `provider_product_url` only.
 *   - Member-safe copy (member_whisper > legacy concierge_whisper).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
    searchEscape,
    getEscapeSignatures,
} from "@/lib/escape-api";
import { destinationBySlug } from "@/components/escape/EscapeTopDestinations";
import EscapeResultsToolbar, { QUICK_CHIPS } from "@/components/escape/EscapeResultsToolbar";
import EscapeRequestModal from "@/components/escape/EscapeRequestModal";
import ConciergeOnDutyPill from "@/components/global/ConciergeOnDutyPill";
import SignatureDetailDrawer from "@/components/escape/SignatureDetailDrawer";

const T = {
    ink: "#0a0a0c", ink2: "#3a3a42", ink3: "#6a6a72",
    paper: "#fdfaf3", bg: "#f7f3ea",
    line: "#e7e3dc", line2: "#d2cdc4",
    brass: "#8a6e2f", card: "#ffffff",
};

const PAGE_SIZE = 24;

const SORT_OPTIONS = [
    { id: "RECOMMENDED",    label: "Recommended",         sort: "DEFAULT",            order: "DESCENDING" },
    { id: "TRAVELLER_HIGH", label: "Traveller rating",    sort: "TRAVELER_RATING",    order: "DESCENDING" },
    { id: "PRICE_ASC",      label: "Price · low to high", sort: "PRICE",              order: "ASCENDING"  },
    { id: "PRICE_DESC",     label: "Price · high to low", sort: "PRICE",              order: "DESCENDING" },
];

export default function EscapeDestinationPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const dest = destinationBySlug(slug);

    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [outage, setOutage] = useState(false);
    const [nextStart, setNextStart] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [totalAvailable, setTotalAvailable] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const [sortId, setSortId] = useState("RECOMMENDED");
    // Quick-chip filters · founder ruling 14 Jun 2026 PM ·
    // "Filters are not working in any inner page · list each separately
    //  please see Viator". Mirrors the /escape main-page chip behaviour:
    // term chips append to the freetext query (q), flag chips push into
    // the Viator `flags` enum (PRIVATE_TOUR, SKIP_THE_LINE).
    const [activeQuickChips, setActiveQuickChips] = useState([]);
    const [signatures, setSignatures] = useState([]);
    const [pickedSignature, setPickedSignature] = useState(null);
    const [requestCtx, setRequestCtx] = useState(null);

    // ── Derive {extraTerms, flags} from the active chips ──────────
    function deriveChipParams(chipLabels) {
        const defs = chipLabels
            .map((lbl) => QUICK_CHIPS.find((c) => c.label === lbl))
            .filter(Boolean);
        return {
            extraTerms: defs.filter((c) => c.kind === "term").map((c) => c.value),
            flags: defs.filter((c) => c.kind === "flag").map((c) => c.value),
        };
    }

    // ── Fetch the first page when slug, sort, or chips change ─────
    const runSearch = useCallback(async (currentSort, chipLabels) => {
        if (!dest) return;
        setLoading(true);
        setOutage(false);
        setResults([]);
        setNextStart(null);
        setHasMore(false);
        const sortDef = SORT_OPTIONS.find((s) => s.id === currentSort) || SORT_OPTIONS[0];
        const { extraTerms, flags } = deriveChipParams(chipLabels || []);
        const combinedQ = [dest.query, extraTerms.join(" ")].filter(Boolean).join(" ").trim();
        try {
            const params = {
                q: combinedQ,
                start: 1,
                count: PAGE_SIZE,
                currency: "INR",
                language: "en-US",
                sort: sortDef.sort,
                order: sortDef.order,
            };
            if (flags.length) params.flags = flags.join(",");
            const data = await searchEscape(params);
            setResults(data.results || []);
            setOutage(!!data.outage);
            setNextStart(data.next_start ?? null);
            setHasMore(!!data.has_more);
            setTotalAvailable(data.total_count || 0);
        } catch {
            setOutage(true);
        } finally {
            setLoading(false);
        }
    }, [dest]);

    useEffect(() => {
        if (!dest) return;
        runSearch(sortId, activeQuickChips);
    }, [dest, sortId, activeQuickChips, runSearch]);

    // ── Signature shelf · relevance-gated by the destination query
    useEffect(() => {
        if (!dest) { setSignatures([]); return; }
        let cancelled = false;
        getEscapeSignatures({ q: dest.query, limit: 6 })
            .then((r) => { if (!cancelled) setSignatures(r.results || []); })
            .catch(() => { if (!cancelled) setSignatures([]); });
        return () => { cancelled = true; };
    }, [dest]);

    const loadMore = useCallback(async () => {
        if (!dest || !hasMore || !nextStart || loadingMore) return;
        setLoadingMore(true);
        const sortDef = SORT_OPTIONS.find((s) => s.id === sortId) || SORT_OPTIONS[0];
        const { extraTerms, flags } = deriveChipParams(activeQuickChips);
        const combinedQ = [dest.query, extraTerms.join(" ")].filter(Boolean).join(" ").trim();
        try {
            const params = {
                q: combinedQ,
                start: nextStart,
                count: PAGE_SIZE,
                currency: "INR",
                language: "en-US",
                sort: sortDef.sort,
                order: sortDef.order,
            };
            if (flags.length) params.flags = flags.join(",");
            const data = await searchEscape(params);
            setResults((prev) => {
                const seen = new Set(prev.map((c) => c.product_code));
                const incoming = (data.results || []).filter((c) => !seen.has(c.product_code));
                return [...prev, ...incoming];
            });
            setNextStart(data.next_start ?? null);
            setHasMore(!!data.has_more);
            setTotalAvailable(data.total_count || 0);
        } catch {
            // soft-fail · keep what we have
        } finally {
            setLoadingMore(false);
        }
    }, [dest, hasMore, nextStart, loadingMore, sortId, activeQuickChips]);

    const onSortChange = useCallback((newSortId) => {
        setSortId(newSortId);
    }, []);

    const toggleQuickChip = useCallback((chip) => {
        // chip can arrive as either a label string or the full QUICK_CHIPS def.
        const label = typeof chip === "string" ? chip : chip?.label;
        if (!label) return;
        setActiveQuickChips((prev) => (
            prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
        ));
    }, []);

    const clearAllFilters = useCallback(() => {
        setActiveQuickChips([]);
    }, []);

    // ── Not-found state for unknown slugs ─────────────────────────
    if (!dest) {
        return (
            <div
                data-testid="escape-destination-unknown"
                style={{ background: T.bg, minHeight: "100vh", padding: "80px 22px" }}
            >
                <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: T.brass, marginBottom: 14 }}>
                        Destination not found
                    </div>
                    <h1 className="t-serif" style={{ fontSize: 36, color: T.ink, marginBottom: 14 }}>
                        We could not find <em>{slug}</em>.
                    </h1>
                    <p style={{ fontSize: 14, color: T.ink2, marginBottom: 30 }}>
                        Let the Concierge® route this one for you · or pick from the curated list.
                    </p>
                    <Link
                        to="/escape"
                        data-testid="escape-destination-back-to-escape"
                        style={{
                            background: T.ink, color: T.paper,
                            borderRadius: 999, padding: "12px 26px",
                            fontSize: 13, letterSpacing: 1.2, textTransform: "uppercase",
                            textDecoration: "none",
                        }}
                    >← Back to /escape</Link>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="escape-destination-page"
            style={{ background: T.bg, minHeight: "100vh" }}
        >
            {/* ── Header ─────────────────────────────────────────── */}
            <header style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "22px 22px 0",
            }}>
                <Link
                    to="/escape"
                    data-testid="escape-destination-back-link"
                    style={{
                        fontFamily: "ui-monospace, monospace", fontSize: 12,
                        letterSpacing: "0.18em", textTransform: "uppercase",
                        color: T.ink2, textDecoration: "none",
                    }}
                >← /escape</Link>
                <ConciergeOnDutyPill />
            </header>

            {/* ── Cinematic hero with destination name ──────────── */}
            <section
                data-testid="escape-destination-hero"
                style={{
                    margin: "22px 22px 32px",
                    borderRadius: 18,
                    overflow: "hidden",
                    border: `1px solid ${T.line2}`,
                    position: "relative",
                    minHeight: 320,
                    background: `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.45) 100%), url(${dest.image}) center/cover no-repeat`,
                }}
            >
                <div style={{
                    position: "absolute", bottom: 36, left: 36, right: 36, color: "#fff",
                }}>
                    <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "rgba(255,255,255,0.85)", marginBottom: 8 }}>
                        Destination · {dest.sub}
                    </div>
                    <h1
                        data-testid="escape-destination-headline"
                        className="t-serif"
                        style={{ fontSize: 48, lineHeight: 1.05, letterSpacing: "-0.02em", margin: 0 }}
                    >
                        Escapes in <em style={{ color: "#f4d990", fontStyle: "italic" }}>{dest.name}</em>
                    </h1>
                    <p style={{ fontSize: 14, color: "rgba(255,255,255,0.85)", marginTop: 12, maxWidth: 560 }}>
                        Tours, attractions and experiences live from Viator · curated by the Concierge® so you can book directly or ask us to arrange it.
                    </p>
                </div>
            </section>

            {/* ── Toolbar + grid ────────────────────────────────── */}
            <section data-testid="escape-destination-results" style={{ padding: "0 22px 80px" }}>
                <div style={{ maxWidth: 1240, margin: "0 auto" }}>
                    <EscapeResultsToolbar
                        resultCount={results.length}
                        queryLabel={dest.name}
                        sortId={sortId}
                        onSortChange={onSortChange}
                        onRefineOpen={() => navigate(`/escape?q=${encodeURIComponent(dest.query)}`)}
                        refineActiveCount={0}
                        activeQuickChips={activeQuickChips}
                        onToggleQuickChip={toggleQuickChip}
                        activeFilterChips={activeQuickChips}
                        onClearAllFilters={clearAllFilters}
                        loading={loading}
                    />

                    {loading && (
                        <div data-testid="escape-destination-loading" style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                            gap: 18, marginTop: 18,
                        }}>
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} style={{
                                    height: 360, borderRadius: 14,
                                    background: T.line, opacity: 0.4,
                                }} />
                            ))}
                        </div>
                    )}

                    {!loading && outage && (
                        <div data-testid="escape-destination-outage" style={{ padding: "40px 12px", textAlign: "center", color: T.ink2 }}>
                            <p>We could not load live experiences for {dest.name} right now.</p>
                            <button
                                data-testid="escape-destination-outage-request"
                                onClick={() => setRequestCtx({
                                    rail: "viator",
                                    title: `${dest.name} (general escape enquiry)`,
                                })}
                                style={{ marginTop: 16, background: T.ink, color: T.paper, border: 0, borderRadius: 14, padding: "12px 22px", fontSize: 14, cursor: "pointer" }}
                            >Ask the Concierge®</button>
                        </div>
                    )}

                    {!loading && !outage && results.length === 0 && (
                        <div data-testid="escape-destination-empty" style={{ padding: "40px 12px", textAlign: "center", color: T.ink2 }}>
                            <p className="t-serif" style={{ fontSize: 16 }}>
                                No live experiences surfaced for {dest.name} just now.
                            </p>
                            <button
                                onClick={() => setRequestCtx({ rail: "viator", title: `${dest.name} (general escape enquiry)` })}
                                style={{ marginTop: 16, background: T.ink, color: T.paper, border: 0, borderRadius: 14, padding: "12px 22px", fontSize: 14, cursor: "pointer" }}
                            >Ask the Concierge®</button>
                        </div>
                    )}

                    {!loading && !outage && results.length > 0 && (
                        <>
                            <div
                                data-testid="escape-destination-grid"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                                    gap: 18, marginTop: 18,
                                }}
                            >
                                {results.map((c) => (
                                    <DestinationCard
                                        key={c.product_code}
                                        card={c}
                                        onRequest={() => setRequestCtx({
                                            rail: "viator",
                                            product_code: c.product_code,
                                            title: c.title || dest.name,
                                            destination: c.destination || dest.name,
                                            country: c.country || "",
                                        })}
                                    />
                                ))}
                            </div>

                            {/* Pagination footer · matches /escape main page */}
                            <div
                                data-testid="escape-destination-pagination"
                                style={{
                                    display: "flex", flexDirection: "column", alignItems: "center",
                                    gap: 10, marginTop: 36, paddingTop: 20,
                                    borderTop: `1px solid ${T.line}`,
                                }}
                            >
                                <div
                                    data-testid="escape-destination-pagination-counter"
                                    style={{ fontSize: 12, color: T.ink3, letterSpacing: 1, textTransform: "uppercase" }}
                                >
                                    Showing {results.length}
                                    {totalAvailable > results.length && (
                                        <> of {totalAvailable.toLocaleString("en-IN")} matches</>
                                    )}
                                </div>
                                {hasMore && (
                                    <button
                                        type="button"
                                        data-testid="escape-destination-load-more"
                                        onClick={loadMore}
                                        disabled={loadingMore}
                                        style={{
                                            background: T.ink, color: T.paper,
                                            border: 0, borderRadius: 999,
                                            padding: "11px 26px", fontSize: 13,
                                            letterSpacing: 1.2, textTransform: "uppercase",
                                            cursor: loadingMore ? "wait" : "pointer",
                                            opacity: loadingMore ? 0.6 : 1,
                                        }}
                                    >{loadingMore ? "Loading…" : "Load more"}</button>
                                )}
                                {!hasMore && results.length >= PAGE_SIZE && (
                                    <p
                                        data-testid="escape-destination-pagination-end"
                                        style={{ fontSize: 12, color: T.ink3, fontStyle: "italic" }}
                                    >You&rsquo;ve reached the end of {dest.name}&rsquo;s live catalogue.</p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {/* ── Signature shelf · relevance-gated ──────────────── */}
            {signatures.length > 0 && (
                <section
                    data-testid="escape-destination-signature-shelf"
                    style={{ padding: "0 22px 80px" }}
                >
                    <div style={{ maxWidth: 1240, margin: "0 auto" }}>
                        <div style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: T.brass, marginBottom: 6 }}>
                                Concierge® Signature Escapes
                            </div>
                            <div className="t-serif" style={{ fontSize: 22, color: T.ink }}>
                                Private routes the Concierge® can arrange around {dest.name}
                            </div>
                        </div>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                            gap: 18,
                        }}>
                            {signatures.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    data-testid={`escape-destination-signature-${s.id}`}
                                    onClick={() => setPickedSignature(s.id)}
                                    style={{
                                        background: T.card, border: `1px solid ${T.line2}`,
                                        borderRadius: 16, overflow: "hidden",
                                        textAlign: "left", cursor: "pointer", padding: 0,
                                        fontFamily: "inherit",
                                    }}
                                >
                                    {s.hero_image_url && (
                                        <div style={{
                                            width: "100%", aspectRatio: "16/10",
                                            background: `url(${s.hero_image_url}) center/cover no-repeat`,
                                        }} />
                                    )}
                                    <div style={{ padding: 14 }}>
                                        <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: T.brass, marginBottom: 5 }}>
                                            Curated by Concierge®
                                        </div>
                                        <div className="t-serif" style={{ fontSize: 17, color: T.ink, marginBottom: 4 }}>
                                            {s.title}
                                        </div>
                                        <div style={{ fontSize: 12, color: T.ink3 }}>
                                            {[s.destination, s.country].filter(Boolean).join(" · ")}
                                        </div>
                                        <p style={{ fontSize: 12, fontStyle: "italic", color: T.ink2, marginTop: 8, lineHeight: 1.5 }}>
                                            {(s.member_whisper || s.concierge_whisper || "").slice(0, 140)}
                                            {((s.member_whisper || s.concierge_whisper || "").length > 140) ? "…" : ""}
                                        </p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Drawers + modals */}
            {pickedSignature && (
                <SignatureDetailDrawer
                    signatureId={pickedSignature}
                    onClose={() => setPickedSignature(null)}
                    onRequest={(s) => {
                        setPickedSignature(null);
                        setRequestCtx({
                            rail: "signature",
                            signature_id: s.id,
                            title: s.title || "",
                            destination: s.destination || dest.name,
                            country: s.country || "",
                        });
                    }}
                />
            )}
            {requestCtx && (
                <EscapeRequestModal
                    open={true}
                    context={requestCtx}
                    onClose={() => setRequestCtx(null)}
                />
            )}
        </div>
    );
}

/** Compact card · mirrors EscapeCard inside Escape.jsx but lives
 *  in this file so the destination page is self-contained. The
 *  detail-drawer flow opens via "View details" → /escape?q=… for
 *  consistency with the rest of the surface (keeps tests stable). */
function DestinationCard({ card, onRequest }) {
    const navigate = useNavigate();
    const price = card.from_price?.amount;
    const currency = card.from_price?.currency || "INR";
    return (
        <article
            data-testid={`escape-destination-card-${card.product_code}`}
            style={{
                background: "#fff",
                border: `1px solid ${T.line}`,
                borderRadius: 16, overflow: "hidden",
                display: "flex", flexDirection: "column",
            }}
        >
            {card.hero_image_url && (
                <div style={{
                    width: "100%", aspectRatio: "16/10",
                    background: `url(${card.hero_image_url}) center/cover no-repeat`,
                }} />
            )}
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8, flexGrow: 1 }}>
                <h3 className="t-serif" style={{ fontSize: 17, color: T.ink, lineHeight: 1.25 }}>
                    {card.title}
                </h3>
                <div style={{ fontSize: 12, color: T.ink3 }}>
                    {[card.destination, card.country].filter(Boolean).join(" · ")}
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 12, color: T.ink2 }}>
                    {card.rating != null && (
                        <span>★ {card.rating.toFixed(1)}{card.review_count != null ? ` (${card.review_count.toLocaleString("en-IN")})` : ""}</span>
                    )}
                    {card.duration_label && <span>⏱ {card.duration_label}</span>}
                </div>
                {price != null && (
                    <div style={{ marginTop: 4, fontSize: 14, color: T.ink }}>
                        From <strong>{currency === "INR" ? "₹" : currency} {price.toLocaleString("en-IN")}</strong>
                    </div>
                )}
                <p style={{ fontSize: 11, fontStyle: "italic", color: T.ink3, marginTop: "auto" }}>
                    Book directly or ask your Concierge® to arrange it.
                </p>
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    {card.url_status === "valid" && card.affiliate_url && (
                        <a
                            href={card.affiliate_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`escape-destination-card-book-${card.product_code}`}
                            style={{
                                background: T.ink, color: "#fff",
                                borderRadius: 999, padding: "8px 16px",
                                fontSize: 12, textDecoration: "none",
                            }}
                        >Continue to booking ↗</a>
                    )}
                    <button
                        type="button"
                        data-testid={`escape-destination-card-request-${card.product_code}`}
                        onClick={onRequest}
                        style={{
                            background: "transparent", color: T.ink,
                            border: `1px solid ${T.line2}`,
                            borderRadius: 999, padding: "8px 16px",
                            fontSize: 12, cursor: "pointer", fontFamily: "inherit",
                        }}
                    >Request via Concierge®</button>
                </div>
            </div>
        </article>
    );
}
